import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Logo } from "./parts";
import { CLINIC, HOURS, telLink, waLink } from "./content";
import "./site.css";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/treatments", label: "Treatments" },
  { to: "/media-gallery", label: "Media Gallery" },
  { to: "/contact", label: "Contact" },
];

function SiteLayout() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.classList.toggle("s-lock", menuOpen);
    return () => document.body.classList.remove("s-lock");
  }, [menuOpen]);

  const onBook = pathname === "/book";

  return (
    <div className="site">
      <a href="#main" className="s-skip">
        Skip to content
      </a>

      <header className={`s-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="container s-header__inner">
          <Logo />
          <nav className="s-nav" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="s-nav__link">
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="s-header__cta">
            <a
              href={telLink}
              className="s-header__phone"
              aria-label={`Call ${CLINIC.phoneDisplay}`}
              title={`Call ${CLINIC.phoneDisplay}`}
            >
              <i className="bi bi-telephone-fill" aria-hidden="true" />
            </a>
            {!onBook ? (
              <Link to="/book" className="s-btn s-btn--primary s-btn--sm">
                Book appointment
              </Link>
            ) : null}
            <button
              type="button"
              className="s-burger"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="s-mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <i
                className={`bi ${menuOpen ? "bi-x-lg" : "bi-list"}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div
          id="s-mobile-menu"
          className={`s-mmenu ${menuOpen ? "is-open" : ""}`}
          hidden={!menuOpen}
        >
          <nav className="container" aria-label="Mobile">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className="s-mmenu__link"
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink to="/book" className="s-mmenu__link">
              Book appointment
            </NavLink>
          </nav>
        </div>
      </header>

      <main id="main" className="s-main">
        <Outlet />
      </main>

      <footer className="s-footer">
        <div className="container s-footer__grid">
          <div className="s-footer__brand">
            <Logo light />
            <p className="s-footer__tag">“{CLINIC.tagline}”</p>
            <div className="s-footer__social">
              <a
                href={CLINIC.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <i className="bi bi-instagram" aria-hidden="true" />
              </a>
              <a
                href={CLINIC.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
              >
                <i className="bi bi-facebook" aria-hidden="true" />
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
              >
                <i className="bi bi-whatsapp" aria-hidden="true" />
              </a>
            </div>
          </div>
          <div>
            <h2 className="s-footer__h">Visit</h2>
            <address className="s-footer__text">
              {CLINIC.address[0]}
              <br />
              {CLINIC.address[1]}
              <br />
              Landmark: {CLINIC.landmark}
            </address>
            <a
              href={CLINIC.mapUrl}
              target="_blank"
              rel="noreferrer"
              className="s-footer__link"
            >
              Open in Google Maps
            </a>
          </div>
          <div>
            <h2 className="s-footer__h">Clinic hours</h2>
            <p className="s-footer__text">
              Monday – Saturday
              <br />
              {HOURS.map((h) => (
                <React.Fragment key={h.label}>
                  {h.label}: {h.text}
                  <br />
                </React.Fragment>
              ))}
              Sunday closed
            </p>
          </div>
          <div>
            <h2 className="s-footer__h">Appointments</h2>
            <p className="s-footer__text">
              <a href={telLink}>{CLINIC.phoneDisplay}</a>
              <br />
              <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>
            </p>
            <Link to="/book" className="s-footer__link">
              Book online
            </Link>
          </div>
        </div>
        <div className="container s-footer__base">
          <span>
            © {new Date().getFullYear()} {CLINIC.name}, Khopoli
          </span>
          <span className="s-footer__basel gap-3">
            <Link to="/about">About</Link>
            <Link to="/treatments">Treatments</Link>
            <Link to="/media-gallery">Media Gallery</Link>
            <Link to="/contact">Contact Us</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </span>
        </div>
      </footer>

      {/* Phone users: the three things people come to do, always one tap away */}
      {!onBook ? (
        <nav className="s-mbar" aria-label="Quick actions">
          <a href={telLink} className="px-3">
            <i className="bi bi-telephone" aria-hidden="true" />
          </a>
          <a href={waLink()} target="_blank" rel="noreferrer" className="is-wa px-3">
            <i className="bi bi-whatsapp" aria-hidden="true" />
          </a>
          <Link to="/book" className="is-book px-5 fw-medium">
            <i className="bi bi-calendar2-check me-1" aria-hidden="true" />
            Book Appointment
          </Link>
        </nav>
      ) : null}
    </div>
  );
}

export default SiteLayout;
